import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal from '../theme/swal';
import { IcMessageCircle, IcClose, IcSend, IcTrash, IcUserCircle } from '../theme/icons';
import noteService from '../services/noteService';
import authService from '../services/authService';
import KI from '../theme/palette';
import { BtnSk, CardSkeleton } from './Skeleton';

const SEEN_KEY = 'dashboard_notes_seen_id';
const POLL_MS = 15000;

// Deterministic accent color per author so each account is recognizable.
const AUTHOR_COLORS = ['text-rose-600', 'text-blue-600', 'text-emerald-600', 'text-orange-600', 'text-amber-600', 'text-teal-600'];
const authorColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 997;
  return AUTHOR_COLORS[h % AUTHOR_COLORS.length];
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Aujourd'hui";
  if (same(d, yest)) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};

const timeLabel = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const DashboardNotesChat = () => {
  const user = authService.getCurrentUser();
  const myId = user?.user_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'manager'; // accès complet

  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);

  const listRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  // Keep the button (and panel) ABOVE the page footer: when the footer scrolls
  // into view, shift everything up by the footer's visible height.
  const [footerOffset, setFooterOffset] = useState(0);
  useEffect(() => {
    const update = () => {
      const footer = document.querySelector('footer');
      if (!footer) { setFooterOffset(0); return; }
      const r = footer.getBoundingClientRect();
      setFooterOffset(Math.max(0, window.innerHeight - r.top));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const markSeen = (list) => {
    const lastId = list.length ? list[list.length - 1].id : 0;
    if (lastId) localStorage.setItem(SEEN_KEY, String(lastId));
    setUnread(0);
  };

  const computeUnread = (list) => {
    const seen = Number(localStorage.getItem(SEEN_KEY) || 0);
    return list.filter((n) => n.id > seen && n.author_id !== myId).length;
  };

  const fetchNotes = useCallback(async () => {
    try {
      const list = await noteService.getNotes();
      const arr = Array.isArray(list) ? list : [];
      setNotes(arr);
      if (openRef.current) markSeen(arr);
      else setUnread(computeUnread(arr));
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load + polling (keeps the unread badge fresh even when closed).
  useEffect(() => {
    fetchNotes();
    const t = setInterval(fetchNotes, POLL_MS);
    return () => clearInterval(t);
  }, [fetchNotes]);

  // Auto-scroll to the newest message whenever the panel shows new content.
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, notes]);

  useEffect(() => {
    if (open) markSeen(notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const created = await noteService.addNote(content);
      setDraft('');
      setNotes((prev) => {
        const next = [...prev, created];
        markSeen(next);
        return next;
      });
    } catch (err) {
      console.error('Error sending note:', err);
      Swal.fire('Erreur', "Impossible d'envoyer la note.", 'error');
    } finally {
      setSending(false);
    }
  };

  const remove = async (note) => {
    const res = await Swal.fire({
      title: 'Supprimer cette note ?',
      text: note.content.length > 80 ? `${note.content.slice(0, 80)}…` : note.content,
      icon: 'warning', showCancelButton: true, confirmButtonColor: KI.coralDark,
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
    });
    if (!res.isConfirmed) return;
    try {
      await noteService.deleteNote(note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
    } catch (err) {
      console.error('Error deleting note:', err);
      Swal.fire('Erreur', 'Impossible de supprimer la note.', 'error');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating chat button — same spot the « Actions rapides » button used to occupy */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fermer les notes' : 'Ouvrir les notes'}
        style={{ bottom: `calc(1.5rem + ${footerOffset}px)` }}
        className="fixed right-6 z-40 inline-flex items-center justify-center w-14 h-14 rounded-full bg-night text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all focus:outline-none"
      >
        {open ? <IcClose className="text-xl" /> : <IcMessageCircle className="text-2xl" />}
        {!open && unread > 0 && (
          // Bulle compteur non-lus (grammaire Ki_Para_Etiquettes : corail =
          // demande attention) ; l'anneau blanc détache la bulle du bouton noir.
          <span className="bdg-count ring-2 ring-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          style={{ bottom: `calc(6rem + ${footerOffset}px)` }}
          className="fixed right-4 sm:right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[min(560px,calc(100vh-8rem))] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="shrink-0 bg-gray-900 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <IcMessageCircle className="text-rose-400 shrink-0" />
              <div className="min-w-0">
                <h2 className="font-semibold leading-tight truncate">Notes — Discussion</h2>
                <p className="text-[11px] text-white/60 truncate">Visible par tous les comptes</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              className="p-2 -mr-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <IcClose />
            </button>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto bg-gray-50 px-3 py-3 space-y-2">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <CardSkeleton />
              </div>
            ) : notes.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <IcMessageCircle className="text-4xl text-gray-200 mb-2" />
                <p className="text-sm text-gray-400">Aucune note pour le moment.</p>
                <p className="text-xs text-gray-400 mt-1">Écrivez la première note pour l'équipe.</p>
              </div>
            ) : (
              notes.map((n, i) => {
                const mine = n.author_id === myId;
                const prevN = notes[i - 1];
                const newDay = !prevN || dayLabel(prevN.created_at) !== dayLabel(n.created_at);
                const sameAuthorAsPrev = prevN && prevN.author_id === n.author_id && !newDay;
                return (
                  <React.Fragment key={n.id}>
                    {newDay && (
                      <div className="flex justify-center py-1">
                        <span className="bdg bdg-neutral">
                          {dayLabel(n.created_at)}
                        </span>
                      </div>
                    )}
                    <div className={`group flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`relative max-w-[85%] ${mine ? 'order-1' : ''}`}>
                        {!mine && !sameAuthorAsPrev && (
                          <p className={`flex items-center gap-1 text-[11px] font-semibold mb-0.5 ml-1 ${authorColor(n.author_name)}`}>
                            <IcUserCircle size={11} /> {n.author_name || 'Compte supprimé'}
                          </p>
                        )}
                        <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm ${
                          mine
                            ? 'bg-gray-900 text-white rounded-br-md'
                            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                        }`}
                        >
                          {n.content}
                          <span className={`block text-right text-[10px] mt-0.5 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                            {timeLabel(n.created_at)}
                          </span>
                        </div>
                        {(mine || isAdmin) && (
                          <button
                            onClick={() => remove(n)}
                            aria-label="Supprimer la note"
                            title="Supprimer"
                            className={`absolute top-1 ${mine ? '-left-7' : '-right-7'} p-1.5 rounded-full text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`}
                          >
                            <IcTrash size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-gray-200 bg-white p-3 flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Écrire une note…"
              aria-label="Écrire une note"
              className="flex-1 resize-none max-h-28 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none"
            />
            <button
              onClick={send}
              disabled={sending || !draft.trim()}
              aria-label="Envoyer la note"
              className="btn-ink shrink-0 w-11 h-11 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none"
            >
              {sending
                ? <BtnSk />
                : <IcSend />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardNotesChat;
